import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Principal } from '../../core/auth/principal.service';
import {
  CampaignPacingDetailResponse,
  LineItemPacingDetailResponse,
  PacingDetailRequestOptions,
  PacingSnapshotRequest,
  PacingSnapshotResponse,
} from '../domain/pacing/pacing.model';

@Injectable({ providedIn: 'root' })
export class PacingService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly principal: Principal,
  ) {}

  fetchActiveSnapshot(): Observable<PacingSnapshotResponse> {
    return this.fetchSnapshot({
      scope: 'ACTIVE',
      includeOrderCrossCheck: false,
      includeBidDiagnostics: false,
    });
  }

  fetchSnapshot(request: PacingSnapshotRequest): Observable<PacingSnapshotResponse> {
    return this.httpClient.post<PacingSnapshotResponse>(
      `${environment.reportingApiUrl}/api/pacing/snapshot`,
      request,
      { headers: this.createAuthHeaders() },
    );
  }

  fetchCampaignDetail(
    campaignId: string,
    options: PacingDetailRequestOptions = {},
  ): Observable<CampaignPacingDetailResponse> {
    return this.httpClient.get<CampaignPacingDetailResponse>(
      `${environment.reportingApiUrl}/api/pacing/campaigns/${campaignId}/detail`,
      {
        headers: this.createAuthHeaders(),
        params: this.createDetailParams(options),
      },
    );
  }

  fetchLineItemDetail(
    lineItemId: string,
    options: PacingDetailRequestOptions = {},
  ): Observable<LineItemPacingDetailResponse> {
    return this.httpClient.get<LineItemPacingDetailResponse>(
      `${environment.reportingApiUrl}/api/pacing/line-items/${lineItemId}/detail`,
      {
        headers: this.createAuthHeaders(),
        params: this.createDetailParams({
          includeOrderCrossCheck: false,
          includeBidDiagnostics: true,
          ...options,
        }),
      },
    );
  }

  private createDetailParams(options: PacingDetailRequestOptions): HttpParams {
    let params = new HttpParams();

    if (options.includeOrderCrossCheck !== undefined) {
      params = params.set('includeOrderCrossCheck', String(options.includeOrderCrossCheck));
    }

    if (options.includeBidDiagnostics !== undefined) {
      params = params.set('includeBidDiagnostics', String(options.includeBidDiagnostics));
    }

    return params;
  }

  private createAuthHeaders(): HttpHeaders {
    const token = this.principal.getToken();
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }
}

export function isReportingApiRequest(url: string): boolean {
  const reportingBase = environment.reportingApiUrl?.replace(/\/$/, '') ?? '';
  return Boolean(reportingBase && url.startsWith(reportingBase));
}
